import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Button, Field, inputStyle, Spinner, Banner, StatTile, date } from '../components/ui';

/**
 * Site matrix — rows are sites, columns are programmes, the cell is this site's
 * most recent work order for that programme.
 *
 * This is where "site 4472 has had no canopy clean in three cycles" becomes
 * visible. State is never carried by colour alone: every cell has a letter and
 * a tooltip, and the legend spells out each state.
 */

const STATES = {
    'complete-evidence': { label: 'Complete + evidence', short: 'E', bg: '#cdebcd', fg: '#046804' },
    'complete': { label: 'Complete', short: 'C', bg: '#e4f6e4', fg: '#046804' },
    'issued': { label: 'Issued', short: 'I', bg: '#e8f1fc', fg: '#1c5cab' },
    'requested': { label: 'Requested', short: 'R', bg: '#f2f1ee', fg: '#52514e' },
    'overdue': { label: 'Overdue', short: 'O', bg: '#fbe0e0', fg: '#a12626' },
    'canceled': { label: 'Canceled', short: 'X', bg: '#f7f2f2', fg: '#898781' },
    'not-due': { label: 'No work order in window', short: '·', bg: '#fff', fg: '#c3c2b7' },
};

export default function MatrixPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const today = new Date().toISOString().substring(0, 10);
    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);

    const [lookups, setLookups] = useState(null);
    const [pmpks, setPmpks] = useState(searchParams.get('pmpks')?.split(',').filter(Boolean) || []);
    const [from, setFrom] = useState(searchParams.get('from') || yearAgo);
    const [to, setTo] = useState(searchParams.get('to') || today);
    const [stateFilter, setStateFilter] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { api.pmLookups().then(setLookups).catch(() => {}); }, []);

    const load = useCallback(() => {
        setLoading(true); setError(null);
        const params = { from, to };
        if (pmpks.length) params.pmpks = pmpks.join(',');
        api.pmMatrix(params).then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
        setSearchParams(params, { replace: true });
    }, [from, to, pmpks, setSearchParams]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const sites = data ? (stateFilter
        ? data.sites.filter(s => Object.values(s.cells).some(c => c.state === stateFilter))
        : data.sites) : [];

    const counts = {};
    if (data) {
        for (const s of data.sites) {
            for (const c of Object.values(s.cells)) counts[c.state] = (counts[c.state] || 0) + 1;
        }
    }

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>Site matrix</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                Sites down, programmes across. Each cell is the site's most recent work order for that programme in the window.
                Retired assets are excluded (they live under ZZZ-Inactive, not behind a status flag).
            </p>

            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
                <Field label="Programmes" hint="blank = every active PM (wide)">
                    <select multiple size={6} value={pmpks}
                        onChange={e => setPmpks([...e.target.selectedOptions].map(o => o.value))}
                        style={{ ...inputStyle, minWidth: 280, height: 'auto' }}>
                        {(lookups?.pms || []).map(p => (
                            <option key={p.PMPK} value={p.PMPK}>{p.PMID} — {p.PMName} ({p.Assets})</option>
                        ))}
                    </select>
                </Field>
                <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></Field>
                <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></Field>
                <Field label="Show only sites with">
                    <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={inputStyle}>
                        <option value="">Any state</option>
                        {Object.entries(STATES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                    </select>
                </Field>
                <Button kind="primary" onClick={load}>Load</Button>
            </div>

            {error && <Banner tone="danger">{error}</Banner>}
            {loading && <Spinner label="Building the matrix" />}

            {data && !loading && (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        <StatTile label="Sites" value={sites.length} sub={stateFilter ? `filtered from ${data.sites.length}` : null} />
                        <StatTile label="Programmes" value={data.programmes.length} />
                        <StatTile label="Overdue cells" value={counts.overdue || 0} tone={counts.overdue ? 'bad' : 'good'} />
                        <StatTile label="No WO in window" value={counts['not-due'] || 0} tone={counts['not-due'] ? 'warn' : undefined} />
                        <StatTile label="Complete with evidence" value={counts['complete-evidence'] || 0} />
                    </div>

                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 12, color: COLORS.ink2 }}>
                        {Object.entries(STATES).map(([k, s]) => (
                            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{
                                    width: 18, height: 18, borderRadius: 4, background: s.bg, color: s.fg,
                                    border: `1px solid ${COLORS.grid}`, fontSize: 11, fontWeight: 700,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }}>{s.short}</span>
                                {s.label}
                            </span>
                        ))}
                    </div>

                    <Panel title={`${sites.length} sites`} style={{ padding: 0 }}>
                        <div style={{ overflow: 'auto', maxHeight: 640 }}>
                            {/* `separate` is required, not cosmetic: a sticky <th> does
                                not hold with border-collapse: collapse. */}
                            <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 }}>
                                <thead>
                                    <tr>
                                        <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 4, background: COLORS.plane, textAlign: 'left', padding: '8px 10px', boxShadow: `inset -1px -1px 0 ${COLORS.axis}` }}>Site</th>
                                        <th style={{ position: 'sticky', top: 0, zIndex: 3, background: COLORS.plane, textAlign: 'left', padding: '8px 10px', boxShadow: `inset 0 -1px 0 ${COLORS.axis}` }}>State</th>
                                        {data.programmes.map(p => (
                                            <th key={p.pmpk} title={p.name} style={{
                                                position: 'sticky', top: 0, zIndex: 3, background: COLORS.plane,
                                                padding: '8px 6px', boxShadow: `inset 0 -1px 0 ${COLORS.axis}`,
                                                fontSize: 11, maxWidth: 90, textAlign: 'center', color: COLORS.ink2,
                                            }}>{p.pmid}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sites.map(s => (
                                        <tr key={s.site}>
                                            <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 2, padding: '5px 10px', borderBottom: `1px solid ${COLORS.grid}`, boxShadow: `inset -1px 0 0 ${COLORS.grid}`, whiteSpace: 'nowrap' }}>
                                                <strong>{s.site}</strong> <span style={{ color: COLORS.muted }}>{s.siteName}</span>
                                            </td>
                                            <td style={{ padding: '5px 10px', borderBottom: `1px solid ${COLORS.grid}`, color: COLORS.muted }}>{s.repairCenter}</td>
                                            {data.programmes.map(p => {
                                                const cell = s.cells[p.pmpk];
                                                if (!cell) return <td key={p.pmpk} style={{ borderBottom: `1px solid ${COLORS.grid}`, background: COLORS.plane }} />;
                                                const st = STATES[cell.state] || STATES['not-due'];
                                                const title = `${p.pmid} @ ${s.site}: ${st.label}${cell.woid ? ` (${cell.woid}, target ${date(cell.targetDate)})` : ''}`;
                                                const inner = (
                                                    <span title={title} style={{
                                                        display: 'block', textAlign: 'center', background: st.bg, color: st.fg,
                                                        fontWeight: 700, fontSize: 11, padding: '4px 0', borderRadius: 4, minWidth: 34,
                                                    }}>{st.short}</span>
                                                );
                                                return (
                                                    <td key={p.pmpk} style={{ padding: '3px 4px', borderBottom: `1px solid ${COLORS.grid}` }}>
                                                        {cell.wopk ? <Link to={`/wo/${cell.wopk}`}>{inner}</Link> : inner}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
}
